import { useState, useMemo, useEffect, useCallback } from "react";
import { opApi, apiError } from "@/lib/api";
import { money } from "@/components/mc/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AddCustomerDialog, matchCustomers } from "@/components/mc/AddCustomerDialog";
import { CustomerLoyaltyCell } from "@/components/mc/CustomerLoyaltyCell";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Minus, Trash2, User, UserPlus, X, Maximize2, Minimize2, Search, Loader2, Gift, Tag, ShoppingCart, ExternalLink } from "lucide-react";

// Mirror of backend _reward_discount so the cashier sees an accurate total before charging.
function cheapestUnit(cart) {
  const prices = (cart || []).map((i) => Number(i.price) || 0).filter((p) => p > 0);
  return prices.length ? Math.min(...prices) : 0;
}
function rewardDiscount(rewardObj, subtotal, cart) {
  if (!rewardObj) return 0;
  const rt = (rewardObj.reward_type || "").toLowerCase();
  const val = Number(rewardObj.reward_value) || 0;
  if (rt === "percent") return Math.round(subtotal * Math.max(0, Math.min(100, val)) / 100 * 100) / 100;
  if (rt === "fixed") return Math.round(Math.min(subtotal, Math.max(0, val)) * 100) / 100;
  if (rt === "free_item") return Math.round(Math.min(subtotal, cheapestUnit(cart)) * 100) / 100;
  if (rt === "bogo") {
    const units = (cart || []).reduce((s, i) => s + (Number(i.qty) || 0), 0);
    return units < 2 ? 0 : Math.round(Math.min(subtotal, cheapestUnit(cart)) * 100) / 100;
  }
  if (rt === "custom") return 0;
  const s = (rewardObj.reward || "").toLowerCase();
  const pct = s.match(/(\d+(?:\.\d+)?)\s*%/);
  if (pct && s.includes("off")) return Math.round(subtotal * parseFloat(pct[1]) / 100 * 100) / 100;
  const amt = s.match(/\$\s*(\d+(?:\.\d+)?)/);
  if (amt) return Math.min(subtotal, parseFloat(amt[1]));
  return 0;
}

export function RegisterTab({ products = [], customers = [], slug, accent, onRefresh, shift, setShift, taxConfig = {}, expandHref, catNodes = [], modGroups = [] }) {
  const [cart, setCart] = useState([]);
  const [sel, setSel] = useState({ g: "all", c: "all", s: "all" });
  const [fullscreen, setFullscreen] = useState(false);
  const [customer, setCustomer] = useState(null);   // {id,name,...} attached record
  const [guestName, setGuestName] = useState("");    // guest ticket (name only)
  const [memberships, setMemberships] = useState([]);
  const [rewardSel, setRewardSel] = useState(null);   // {memId, idx, reward, title}
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);          // {code, discount, stackable_with_loyalty}
  const [promoList, setPromoList] = useState([]);
  const [notes, setNotes] = useState("");
  const [employees, setEmployees] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [custSearch, setCustSearch] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [pinFor, setPinFor] = useState(null);
  const [pin, setPin] = useState("");
  const [guestDraft, setGuestDraft] = useState("");
  const [checkoutStep, setCheckoutStep] = useState(false);
  const employee = shift;
  const hasCustomer = !!customer || !!guestName.trim();

  const available = useMemo(() => products.filter((p) => {
    if (p.available === false) return false;
    // Only show items sold at the register. Legacy items with no channels set stay visible.
    const ch = p.channels;
    if (!ch || ch.length === 0) return true;
    return ch.includes("pos");
  }), [products]);
  const hasTree = catNodes.length > 0;
  const kids = (pid) => catNodes.filter((n) => (n.parent_id || null) === (pid || null)).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const flatCats = useMemo(() => [...new Set(available.map((p) => p.category).filter(Boolean))], [available]);
  const availGroups = useMemo(() => new Set(available.map((p) => p.group).filter(Boolean)), [available]);
  const l1nodes = hasTree ? kids(null).filter((n) => availGroups.has(n.name)) : [];
  const selG = l1nodes.find((n) => n.name === sel.g);
  const l2nodes = selG ? kids(selG.id) : [];
  const selC = l2nodes.find((n) => n.name === sel.c);
  const l3nodes = selC ? kids(selC.id) : [];
  const shown = !hasTree
    ? (sel.g === "all" ? available : available.filter((p) => (p.category || "") === sel.g))
    : available.filter((p) =>
        (sel.g === "all" || (p.group || "") === sel.g) &&
        (sel.c === "all" || (p.category || "") === sel.c) &&
        (sel.s === "all" || (p.subcategory || "") === sel.s));

  useEffect(() => { opApi.get("/tenant/employees").then((r) => setEmployees((r.data || []).filter((e) => e.active))).catch(() => {}); }, []);

  const loadMemberships = useCallback((cid) => {
    if (!cid) { setMemberships([]); return; }
    opApi.get(`/tenant/customers/${cid}/loyalty`).then((r) => setMemberships(r.data || [])).catch(() => setMemberships([]));
  }, []);

  const subtotal = useMemo(() => Math.round(cart.reduce((s, i) => s + i.price * i.qty, 0) * 100) / 100, [cart]);
  const rewardOptions = memberships.flatMap((m) => {
    const rr = m.ready_rewards || [];
    if (rr.length) return rr.map((r, idx) => ({ memId: m.id, idx, reward: r.reward, reward_type: r.reward_type, reward_value: r.reward_value, title: m.program_title || m.title, goal: r.goal }));
    if ((m.rewards_ready || 0) > 0) return [{ memId: m.id, idx: 0, reward: m.reward, title: m.program_title || m.title }];
    return [];
  });
  const rDiscount = rewardSel ? rewardDiscount(rewardSel, subtotal, cart) : 0;
  const loyaltyColor = memberships.some((m) => (m.rewards_ready || 0) > 0) ? "green" : (memberships.length > 0 ? "blue" : "grey");
  const pDiscount = promo ? promo.discount : 0;
  const afterDiscount = Math.max(0, Math.round((subtotal - rDiscount - pDiscount) * 100) / 100);
  const catRates = taxConfig.categories || {};
  const defRate = taxConfig.enabled ? (parseFloat(taxConfig.rate) || 0) : 0;
  const taxFactor = subtotal > 0 ? afterDiscount / subtotal : 0;
  const tax = !taxConfig.enabled ? 0 : Math.round(cart.reduce((s, i) => {
    if (i.taxable === false) return s;
    const r = (i.category in catRates) ? (parseFloat(catRates[i.category]) || 0) : defRate;
    return r > 0 ? s + i.price * i.qty * taxFactor * r : s;
  }, 0)) / 100;
  const total = Math.round((afterDiscount + tax) * 100) / 100;

  const groupsFor = (p) => (p.modifier_group_ids || []).map((id) => modGroups.find((g) => g.id === id)).filter(Boolean);
  const priceGroupOf = (p) => (p.variant_options || []).find((o) => o.price_driving && (o.values || []).length) || null;
  const variantMinOf = (p) => { const g = priceGroupOf(p); if (!g) return null; const ps = (g.values || []).map((v) => Number(v.price) || 0); return ps.length ? Math.min(...ps) : null; };
  const fromPriceOf = (p) => { const m = variantMinOf(p); return m != null ? m : (Number(p.price) || 0); };
  const lineKey = (pid, modSel, variants) => pid + "|" + (modSel || []).map((m) => m.group_id + ":" + m.option_id).sort().join(",") + "|" + (variants || []).map((v) => v.group + ":" + v.value).sort().join(",");
  const addLine = (p, modSel, detail, variants, varLabel) => {
    const pg = priceGroupOf(p);
    let base = Number(p.price) || 0;
    if (pg) { const sv = (variants || []).find((v) => v.group === pg.name); const val = sv && (pg.values || []).find((x) => x.name === sv.value); base = val ? Number(val.price) || 0 : base; }
    let delta = 0; (detail || []).forEach((d) => { delta += Number(d.price_delta) || 0; });
    const unit = Math.round((base + delta) * 100) / 100;
    const dispName = p.name + (varLabel ? ` (${varLabel})` : "");
    const key = lineKey(p.id, modSel, variants);
    setCart((c) => {
      const ex = c.find((i) => i.key === key);
      if (ex) return c.map((i) => i.key === key ? { ...i, qty: i.qty + 1 } : i);
      return [...c, { key, product_id: p.id, name: dispName, product_name: p.name, price: unit, base_price: base, qty: 1,
        taxable: p.taxable !== false, category: p.category || null, modSel: modSel || [], modifiers: detail || [],
        variants: variants || [], varLabel: varLabel || "" }];
    });
  };
  const addItem = (p) => {
    const groups = groupsFor(p);
    if (groups.length || (p.variant_options || []).length) { openModPicker(p, groups); return; }
    addLine(p, [], [], [], "");
  };
  const setQty = (key, d) => setCart((c) => c.map((i) => i.key === key ? { ...i, qty: Math.max(0, i.qty + d) } : i).filter((i) => i.qty > 0));

  // ---- modifier + variant picker ----
  const [modProd, setModProd] = useState(null);
  const [modList, setModList] = useState([]);
  const [modSel, setModSel] = useState({});   // { group_id: [option_id,...] }
  const [varSel, setVarSel] = useState({});   // { dimensionName: value }
  const openModPicker = (p, groups) => {
    const init = {};
    groups.forEach((g) => { if (g.select_type === "single" && g.required && (g.options || [])[0]) init[g.id] = [g.options[0].id]; else init[g.id] = []; });
    setModProd(p); setModList(groups); setModSel(init); setVarSel({});
  };
  const setVar = (name, val, required) => setVarSel((s) => ({ ...s, [name]: (s[name] === val && !required) ? undefined : val }));
  const toggleMod = (g, oid) => {
    setModSel((s) => {
      const cur = s[g.id] || [];
      if (g.select_type === "single") return { ...s, [g.id]: cur[0] === oid ? (g.required ? cur : []) : [oid] };
      const nx = cur.includes(oid) ? cur.filter((x) => x !== oid) : [...cur, oid];
      return { ...s, [g.id]: nx };
    });
  };
  const modUnit = (() => {
    if (!modProd) return 0;
    const pg = priceGroupOf(modProd); let base = Number(modProd.price) || 0;
    if (pg) { const v = (pg.values || []).find((x) => x.name === varSel[pg.name]); base = v ? Number(v.price) || 0 : (variantMinOf(modProd) ?? base); }
    let d = 0;
    modList.forEach((g) => (modSel[g.id] || []).forEach((oid) => { const o = (g.options || []).find((x) => x.id === oid); if (o) d += Number(o.price_delta) || 0; }));
    return Math.round((base + d) * 100) / 100;
  })();
  const confirmMod = () => {
    const variants = []; const labels = [];
    for (const vo of (modProd.variant_options || [])) {
      const chosen = varSel[vo.name];
      if (vo.price_driving && !chosen) { toast.error(`Choose a ${vo.name}.`); return; }
      if (chosen) { variants.push({ group: vo.name, value: chosen }); labels.push(chosen); }
    }
    for (const g of modList) {
      const c = (modSel[g.id] || []).length;
      const mn = g.select_type === "multi" ? (g.min_select || 0) : (g.required ? 1 : 0);
      const mx = g.select_type === "single" ? 1 : (g.max_select || 0);
      if (c < mn) { toast.error(`Choose ${g.select_type === "single" ? "an option" : `at least ${mn}`} for “${g.name}”.`); return; }
      if (mx && c > mx) { toast.error(`Choose at most ${mx} for “${g.name}”.`); return; }
    }
    const detail = [];
    modList.forEach((g) => (modSel[g.id] || []).forEach((oid) => { const o = (g.options || []).find((x) => x.id === oid); if (o) detail.push({ group_id: g.id, group_name: g.name, option_id: o.id, option_name: o.name, price_delta: Number(o.price_delta) || 0 }); }));
    const sel = detail.map((d) => ({ group_id: d.group_id, option_id: d.option_id }));
    addLine(modProd, sel, detail, variants, labels.join(", "));
    setModProd(null); setModList([]); setModSel({}); setVarSel({});
  };
  const clearAll = () => { setCart([]); setCustomer(null); setGuestName(""); setMemberships([]); setRewardSel(null); setPromo(null); setPromoCode(""); setNotes(""); setCheckoutStep(false); };

  const applyGuest = () => { if (guestDraft.trim()) { setGuestName(guestDraft.trim()); setCustOpen(false); setGuestDraft(""); } };

  const attachCustomer = (c) => { setCustomer(c); setGuestName(""); setCustOpen(false); setCustSearch(""); loadMemberships(c.id); setRewardSel(null); setPromo(null); };

  const applyPromo = async (codeArg) => {
    const code = (typeof codeArg === "string" ? codeArg : promoCode).trim();
    if (!code) return;
    if (cart.length === 0) { toast.error("Add items first."); return; }
    try {
      const { data } = await opApi.post("/tenant/promos/validate", {
        code: code, items: cart, customer_id: customer?.id || null });
      if (rewardSel && !data.stackable_with_loyalty) { toast.error("This code can't be combined with a loyalty reward."); return; }
      setPromoCode(code); setPromo(data); toast.success(`Code applied — ${money(data.discount)} off`);
    } catch (e) { setPromo(null); toast.error(apiError(e.response?.data?.detail)); }
  };
  const loadPromoList = async () => {
    try { const { data } = await opApi.get("/tenant/promos"); setPromoList(data || []); }
    catch (e) { setPromoList([]); }
  };
  const promoEligibility = (p) => {
    if (cart.length === 0) return { ok: false, reason: "Add items" };
    const now = new Date();
    if (p.starts_at && new Date(p.starts_at) > now) return { ok: false, reason: "Not started" };
    if (p.ends_at && new Date(p.ends_at) < now) return { ok: false, reason: "Expired" };
    if (p.min_subtotal && subtotal < p.min_subtotal) return { ok: false, reason: `Min ${money(p.min_subtotal)}` };
    if (p.applies_to === "category" && p.target_id && !cart.some((i) => (i.category || "") === p.target_id)) return { ok: false, reason: `${p.target_id} only` };
    if (p.applies_to === "product" && p.target_id && !cart.some((i) => i.product_id === p.target_id)) return { ok: false, reason: "Item not in cart" };
    return { ok: true, reason: "" };
  };

  const checkout = async (completeNow) => {
    if (cart.length === 0) { toast.error("The ticket is empty."); return; }
    setProcessing(true);
    try {
      const { data } = await opApi.post("/tenant/orders", {
        items: cart.map((i) => ({ product_id: i.product_id, name: i.product_name || i.name, price: i.price, qty: i.qty, modifiers: i.modSel || [], variants: i.variants || [] })),
        customer_id: customer?.id || null,
        guest_name: customer ? null : (guestName.trim() || null),
        fulfillment: "pickup_now", notes: notes.trim(),
        promo_code: promo ? promo.code : null, reward_membership_id: rewardSel ? rewardSel.memId : null, reward_index: rewardSel ? rewardSel.idx : null,
        employee_id: employee?.id || null, complete_now: completeNow });
      toast.success(`Order #${data.order_number} · ${money(data.total)} ${completeNow ? "completed" : "sent to board"}`);
      clearAll(); onRefresh && onRefresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setProcessing(false); }
  };

  const handleCheckoutClick = () => {
    if (cart.length === 0) return;
    if (!hasCustomer) {
      toast.error("Add a customer or guest name before checking out.");
      setCustOpen(true); setCustSearch(""); setGuestDraft("");
      return;
    }
    setCheckoutStep(true);
  };

  const startShift = (emp) => { setPinFor(emp); setPin(""); };
  const verifyPin = async () => {
    try {
      const { data } = await opApi.post("/tenant/employees/verify-pin", { employee_id: pinFor.id, pin });
      setShift(data); setPinFor(null); setShiftOpen(false); setPin(""); toast.success(`${data.name} is on shift`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const filteredCusts = (() => {
    const q = custSearch.trim();
    if (!q) return [];
    const digits = q.replace(/\D/g, "");
    if (digits.length >= 3) return customers.filter((c) => (c.phone || "").replace(/\D/g, "").includes(digits)).slice(0, 8);
    const ql = q.toLowerCase();
    return customers.filter((c) => (c.name || "").toLowerCase().includes(ql) || (c.email || "").toLowerCase().includes(ql)).slice(0, 8);
  })();

  // Route the register search term to the right field when creating a new customer.
  const custDraft = (() => {
    const q = (custSearch || "").trim();
    if (!q) return { phone: "", email: "", name: "" };
    if (q.includes("@")) return { phone: "", email: q, name: "" };
    if (q.replace(/\D/g, "").length >= 3) return { phone: q, email: "", name: "" };
    return { phone: "", email: "", name: q };
  })();

  return (
    <div className={fullscreen ? "fixed inset-0 z-50 bg-background overflow-auto p-5" : ""} data-testid="register-tab">
      {/* header */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-primary" />
          <h2 className="font-display font-bold text-xl">Register</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-none gap-1.5" onClick={() => setShiftOpen(true)} data-testid="register-shift-btn">
            <User className="h-4 w-4" /> {employee ? `On shift: ${employee.name}` : "Clock in"}
          </Button>
          <Button variant="outline" size="sm" className="rounded-none gap-1.5" onClick={() => expandHref ? window.open(expandHref, "_blank") : setFullscreen((f) => !f)} data-testid="register-fullscreen-btn">
            {expandHref ? <ExternalLink className="h-4 w-4" /> : (fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />)} {expandHref ? "Full screen" : (fullscreen ? "Exit" : "Full screen")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
        {/* product grid */}
        <div>
          <div className="space-y-1.5 mb-3">
            <div className="flex gap-1.5 flex-wrap">
              {(() => {
                const chip = (label, active, onClick, testid) => (
                  <button key={testid} onClick={onClick} data-testid={testid}
                    className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${active ? "bg-primary text-white border-primary" : "border-border hover:bg-secondary"}`}>{label}</button>
                );
                if (!hasTree) {
                  return [chip("all", sel.g === "all", () => setSel({ g: "all", c: "all", s: "all" }), "register-cat-all"),
                    ...flatCats.map((c) => chip(c, sel.g === c, () => setSel({ g: c, c: "all", s: "all" }), `register-cat-${c}`))];
                }
                return [chip("all", sel.g === "all", () => setSel({ g: "all", c: "all", s: "all" }), "register-cat-all"),
                  ...l1nodes.map((n) => chip(n.name, sel.g === n.name, () => setSel({ g: n.name, c: "all", s: "all" }), `register-cat-g-${n.name}`))];
              })()}
            </div>
            {hasTree && selG && l2nodes.length > 0 && (
              <div className="flex gap-1.5 flex-wrap pl-2 border-l-2 border-primary/30" data-testid="register-cat-row-2">
                <button onClick={() => setSel({ ...sel, c: "all", s: "all" })} data-testid="register-cat-c-all"
                  className={`px-2.5 py-1 text-[11px] tracking-wide border transition-colors ${sel.c === "all" ? "bg-secondary border-primary" : "border-border hover:bg-secondary"}`}>All {sel.g}</button>
                {l2nodes.map((n) => (
                  <button key={n.id} onClick={() => setSel({ ...sel, c: n.name, s: "all" })} data-testid={`register-cat-c-${n.name}`}
                    className={`px-2.5 py-1 text-[11px] tracking-wide border transition-colors ${sel.c === n.name ? "bg-primary text-white border-primary" : "border-border hover:bg-secondary"}`}>{n.name}</button>
                ))}
              </div>
            )}
            {hasTree && selC && l3nodes.length > 0 && (
              <div className="flex gap-1.5 flex-wrap pl-4 border-l-2 border-primary/20" data-testid="register-cat-row-3">
                <button onClick={() => setSel({ ...sel, s: "all" })} data-testid="register-cat-s-all"
                  className={`px-2.5 py-1 text-[11px] tracking-wide border transition-colors ${sel.s === "all" ? "bg-secondary border-primary" : "border-border hover:bg-secondary"}`}>All {sel.c}</button>
                {l3nodes.map((n) => (
                  <button key={n.id} onClick={() => setSel({ ...sel, s: n.name })} data-testid={`register-cat-s-${n.name}`}
                    className={`px-2.5 py-1 text-[11px] tracking-wide border transition-colors ${sel.s === n.name ? "bg-primary text-white border-primary" : "border-border hover:bg-secondary"}`}>{n.name}</button>
                ))}
              </div>
            )}
          </div>
          {shown.length === 0 ? (
            <div className="border border-dashed border-border py-12 text-center text-sm text-muted-foreground">No available products. Add items in the Products tab.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5">
              {shown.map((p) => (
                <button key={p.id} onClick={() => addItem(p)} data-testid={`register-product-${p.id}`}
                  className="border border-border bg-card p-3 text-left hover:border-primary hover:shadow-sm transition-all active:scale-[0.98] min-h-[84px] flex flex-col justify-between">
                  <span className="text-sm font-medium leading-tight line-clamp-2">{p.name}</span>
                  <span className="font-mono text-sm font-bold mt-2" style={{ color: accent || undefined }}>{money(p.price)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ticket */}
        <div className="border border-border bg-card flex flex-col" data-testid="register-ticket">
          {/* items */}
          <div className="flex-1 overflow-auto max-h-[46vh] min-h-[120px]">
            {cart.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground" data-testid="register-empty-ticket">Tap products to build the ticket.</div>
            ) : cart.map((i) => (
              <div key={i.key} className="flex items-center gap-2 px-3 py-2 border-b border-border" data-testid={`register-line-${i.product_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{i.name}</div>
                  {(i.modifiers || []).length > 0 && <div className="text-[11px] text-muted-foreground truncate">{i.modifiers.map((m) => m.option_name).join(", ")}</div>}
                  <div className="text-xs text-muted-foreground font-mono">{money(i.price)}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" className="h-7 w-7 rounded-none" onClick={() => setQty(i.key, -1)} data-testid={`register-minus-${i.product_id}`}><Minus className="h-3 w-3" /></Button>
                  <span className="w-6 text-center text-sm font-mono">{i.qty}</span>
                  <Button variant="outline" size="icon" className="h-7 w-7 rounded-none" onClick={() => setQty(i.key, 1)} data-testid={`register-plus-${i.product_id}`}><Plus className="h-3 w-3" /></Button>
                </div>
                <span className="w-16 text-right font-mono text-sm font-semibold">{money(i.price * i.qty)}</span>
              </div>
            ))}
          </div>

          {/* customer */}
          <div className={`p-3 border-t transition-colors ${hasCustomer ? "!bg-green-500/10 !border-green-500" : "border-border"}`}>
            {customer ? (
              <div className="flex items-center gap-2" data-testid="register-customer-attached">
                <User className="h-4 w-4 !text-green-600" />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{customer.name}</div><div className="text-xs text-muted-foreground truncate">{customer.phone || customer.email || ""}</div></div>
                <span data-testid="register-loyalty-icon"><CustomerLoyaltyCell customerId={customer.id} color={loyaltyColor} readOnly onChange={() => loadMemberships(customer.id)} /></span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setCustomer(null); setMemberships([]); setRewardSel(null); }} data-testid="register-customer-clear"><X className="h-4 w-4" /></Button>
              </div>
            ) : guestName ? (
              <div className="flex items-center gap-2" data-testid="register-guest-attached">
                <User className="h-4 w-4 !text-green-600" />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{guestName}</div><div className="text-xs text-muted-foreground">Guest ticket</div></div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setGuestName("")} data-testid="register-guest-clear"><X className="h-4 w-4" /></Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" className={`rounded-none gap-1.5 w-full justify-center transition-colors ${cart.length > 0 ? "!bg-red-500 !text-white !border-red-500 hover:!bg-red-600 attn-pulse" : ""}`} onClick={() => { setCustOpen(true); setCustSearch(""); setGuestDraft(""); }} data-testid="register-attach-customer">
                <UserPlus className="h-4 w-4" /> Add customer
              </Button>
            )}
          </div>

          {/* loyalty reward */}
          {rewardOptions.length > 0 && (
            <div className="px-3 py-2 border-t border-border">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><Gift className="h-3 w-3" /> Reward{rewardOptions.length > 1 ? "s" : ""} available</div>
              {rewardOptions.map((o) => {
                const on = rewardSel && rewardSel.memId === o.memId && rewardSel.idx === o.idx;
                return (
                  <label key={`${o.memId}-${o.idx}`} className="flex items-center gap-2 text-sm cursor-pointer py-0.5" data-testid={`register-reward-${o.memId}-${o.idx}`}>
                    <input type="radio" name="reward" checked={!!on} onChange={() => { setRewardSel(on ? null : o); if (promo && !promo.stackable_with_loyalty) setPromo(null); }} />
                    <span className="truncate">{o.title}: {o.reward || "reward"}</span>
                  </label>
                );
              })}
              {rewardSel && <button className="text-xs text-muted-foreground underline mt-1" onClick={() => setRewardSel(null)}>Remove reward</button>}
            </div>
          )}

          {/* promo */}
          <div className="px-3 py-2 border-t border-border">
            {promo ? (
              <div className="flex items-center gap-2 text-sm" data-testid="register-promo-applied">
                <Tag className="h-4 w-4 text-emerald-600" />
                <span className="font-mono font-semibold">{promo.code}</span>
                <span className="text-muted-foreground">−{money(promo.discount)}</span>
                <button className="ml-auto text-muted-foreground hover:text-foreground" onClick={() => { setPromo(null); setPromoCode(""); }} data-testid="register-promo-remove"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input className="rounded-none h-9 text-sm font-mono uppercase" placeholder="Promo code" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} data-testid="register-promo-input" />
                <Popover onOpenChange={(o) => { if (o) loadPromoList(); }}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-none h-9 w-9 shrink-0" title="Browse promos" data-testid="register-promo-lookup"><Tag className="h-4 w-4" /></Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0 rounded-none" align="end" data-testid="register-promo-lookup-panel">
                    <div className="px-3 py-2 border-b border-border text-xs font-bold uppercase tracking-wider">Available promos</div>
                    <div className="max-h-64 overflow-y-auto">
                      {promoList.filter((p) => p.active !== false).length === 0 ? (
                        <div className="px-3 py-4 text-xs text-muted-foreground text-center">No active promos.</div>
                      ) : [...promoList.filter((p) => p.active !== false)]
                        .map((p) => ({ p, elig: promoEligibility(p) }))
                        .sort((a, b) => (b.elig.ok === a.elig.ok ? 0 : b.elig.ok ? 1 : -1))
                        .map(({ p, elig }) => (
                        <button key={p.id} type="button" disabled={!elig.ok} onClick={() => elig.ok && applyPromo(p.code)}
                          className={`w-full text-left px-3 py-2 border-b border-border ${elig.ok ? "hover:bg-secondary cursor-pointer" : "opacity-45 cursor-not-allowed"}`}
                          title={elig.ok ? "Apply this code" : `Not eligible — ${elig.reason}`} data-testid={`register-promo-option-${p.code}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono font-bold text-sm">{p.code}</span>
                            {elig.ok
                              ? <span className="text-xs font-semibold text-emerald-700">{p.type === "percent" ? `${p.value}% off` : p.type === "free_item" ? "Free item" : `${money(p.value)} off`}</span>
                              : <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{elig.reason}</span>}
                          </div>
                          {p.min_subtotal ? <div className="text-[10px] text-muted-foreground">Min {money(p.min_subtotal)}{p.applies_to === "category" && p.target_id ? ` · ${p.target_id}` : ""}</div> : (p.applies_to === "category" && p.target_id ? <div className="text-[10px] text-muted-foreground">{p.target_id} only</div> : null)}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button variant="outline" size="sm" className="rounded-none shrink-0" onClick={() => applyPromo()} data-testid="register-promo-apply">Apply</Button>
              </div>
            )}
          </div>

          {/* totals + checkout */}
          <div className="p-3 border-t border-border space-y-1.5">
            <div className="flex justify-between text-sm text-muted-foreground"><span>Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
            {rDiscount > 0 && <div className="flex justify-between text-sm text-emerald-700"><span>Reward</span><span className="font-mono">−{money(rDiscount)}</span></div>}
            {pDiscount > 0 && <div className="flex justify-between text-sm text-emerald-700"><span>Promo</span><span className="font-mono">−{money(pDiscount)}</span></div>}
            {tax > 0 && <div className="flex justify-between text-sm text-muted-foreground"><span>{taxConfig.label || "Tax"}</span><span className="font-mono">{money(tax)}</span></div>}
            <div className="flex justify-between text-lg font-bold"><span>Total</span><span className="font-mono" data-testid="register-total">{money(total)}</span></div>
            <Input className="rounded-none h-9 text-sm mt-1" placeholder="Order note (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="register-notes" />
            {!checkoutStep ? (
              <>
                <Button className="rounded-none w-full h-11 text-base mt-1" disabled={cart.length === 0} onClick={handleCheckoutClick} data-testid="register-checkout">
                  Check out · {money(total)}
                </Button>
                {cart.length > 0 && <button className="text-xs text-muted-foreground underline w-full text-center pt-1" onClick={clearAll} data-testid="register-clear">Clear ticket</button>}
              </>
            ) : (
              <div className="space-y-2 pt-1" data-testid="register-checkout-step">
                <div className="text-center text-xs text-muted-foreground">Payment: Cash / pay in person · goes to the order board on submit</div>
                <Button className="rounded-none w-full h-11 text-base" disabled={processing} onClick={() => checkout(false)} data-testid="register-submit">
                  {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Submit order · ${money(total)}`}
                </Button>
                <Button variant="outline" className="rounded-none w-full" disabled={processing} onClick={() => setCheckoutStep(false)} data-testid="register-checkout-back">Back to ticket</Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* customer search / guest dialog */}
      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        <DialogContent className="max-w-md" data-testid="register-customer-dialog">
          <DialogHeader><DialogTitle>Add customer to ticket</DialogTitle><DialogDescription>Search by phone (or email). No account? Add a guest name below.</DialogDescription></DialogHeader>
          <Input autoFocus className="rounded-none" placeholder="Search phone or email…" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} data-testid="register-customer-search" />
          {custSearch.trim() && (
            <div className="max-h-52 overflow-auto border border-border divide-y divide-border">
              {filteredCusts.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">No match found.</div>}
              {filteredCusts.map((c) => (
                <div key={c.id} className="flex items-center gap-2 px-3 py-2" data-testid={`register-cust-result-${c.id}`}>
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{c.name}</div><div className="text-xs text-muted-foreground truncate">{c.phone || c.email}</div></div>
                  <Button size="sm" className="rounded-none shrink-0" onClick={() => attachCustomer(c)} data-testid={`register-cust-use-${c.id}`}>Use</Button>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" className="rounded-none gap-1.5" onClick={() => { setCustOpen(false); setAddOpen(true); }} data-testid="register-new-customer"><UserPlus className="h-4 w-4" /> Create new customer</Button>
          <div className="border-t border-border pt-3">
            <Label className="text-xs">No account? Add a guest name</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <Input className="rounded-none" placeholder="Guest name" value={guestDraft} onChange={(e) => setGuestDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyGuest()} data-testid="register-guest-input" />
              <Button variant="outline" className="rounded-none shrink-0" onClick={applyGuest} data-testid="register-guest-use">Use guest</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AddCustomerDialog open={addOpen} onOpenChange={setAddOpen} defaultName={custDraft.name} defaultPhone={custDraft.phone} defaultEmail={custDraft.email} customers={customers}
        onCreated={(c) => { attachCustomer(c); onRefresh && onRefresh(); }} />

      {/* modifier picker */}
      <Dialog open={!!modProd} onOpenChange={(o) => { if (!o) { setModProd(null); setModList([]); setModSel({}); setVarSel({}); } }}>
        <DialogContent className="rounded-none max-w-md max-h-[85vh] overflow-y-auto" data-testid="register-modifier-dialog">
          {modProd && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{modProd.name}</DialogTitle>
                <DialogDescription>Choose options for this item.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {(modProd.variant_options || []).map((vo) => (
                  <div key={vo.name} data-testid={`register-var-group-${vo.name}`}>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{vo.name} {vo.price_driving ? "· required" : "· optional"}</div>
                    <div className="space-y-1">
                      {(vo.values || []).map((v) => {
                        const on = varSel[vo.name] === v.name;
                        return (
                          <button key={v.name} type="button" onClick={() => setVar(vo.name, v.name, vo.price_driving)} data-testid={`register-var-opt-${vo.name}-${v.name}`}
                            className={`w-full flex items-center justify-between px-3 py-2 border text-sm transition-colors ${on ? "border-primary bg-primary/5" : "border-border hover:bg-secondary"}`}>
                            <span className="flex items-center gap-2"><span className={`inline-block h-3.5 w-3.5 border rounded-full ${on ? "bg-primary border-primary" : "border-muted-foreground"}`} />{v.name}</span>
                            {vo.price_driving ? <span className="font-mono text-xs text-muted-foreground">{money(v.price)}</span> : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {modList.map((g) => (
                  <div key={g.id} data-testid={`register-mod-group-${g.id}`}>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                      {g.name} {g.select_type === "single" && g.required ? "· required" : g.select_type === "multi" ? `· pick ${g.min_select || 0}${g.max_select ? `–${g.max_select}` : "+"}` : "· optional"}
                    </div>
                    <div className="space-y-1">
                      {(g.options || []).map((o) => {
                        const on = (modSel[g.id] || []).includes(o.id);
                        return (
                          <button key={o.id} type="button" onClick={() => toggleMod(g, o.id)} data-testid={`register-mod-opt-${o.id}`}
                            className={`w-full flex items-center justify-between px-3 py-2 border text-sm transition-colors ${on ? "border-primary bg-primary/5" : "border-border hover:bg-secondary"}`}>
                            <span className="flex items-center gap-2">
                              <span className={`inline-block h-3.5 w-3.5 border ${g.select_type === "single" ? "rounded-full" : ""} ${on ? "bg-primary border-primary" : "border-muted-foreground"}`} />
                              {o.name}
                            </span>
                            {Number(o.price_delta) ? <span className="font-mono text-xs text-muted-foreground">+{money(o.price_delta)}</span> : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="font-mono font-bold text-lg">{money(modUnit)}</span>
                <Button className="rounded-none" onClick={confirmMod} data-testid="register-mod-confirm">Add to ticket</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>


      {/* shift / clock-in dialog */}
      <Dialog open={shiftOpen} onOpenChange={(o) => { setShiftOpen(o); if (!o) setPinFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="register-shift-dialog">
          <DialogHeader><DialogTitle>{pinFor ? `Enter PIN for ${pinFor.name}` : "Who's on shift?"}</DialogTitle></DialogHeader>
          {employees.length === 0 ? (
            <p className="text-sm text-muted-foreground">No staff added yet. Add cashiers under Settings → Staff. You can still take orders — they'll be recorded without a name.</p>
          ) : pinFor ? (
            <div className="space-y-3">
              <Input autoFocus type="password" inputMode="numeric" maxLength={6} className="rounded-none text-center text-lg tracking-[0.5em]" value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && verifyPin()} data-testid="register-pin-input" />
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="rounded-none" onClick={() => setPinFor(null)}>Back</Button>
                <Button className="rounded-none" onClick={verifyPin} data-testid="register-pin-submit">Start shift</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {employees.map((e) => (
                <button key={e.id} className="w-full flex items-center gap-2 px-3 py-2.5 border border-border hover:bg-secondary text-left" onClick={() => startShift(e)} data-testid={`register-emp-${e.id}`}>
                  <User className="h-4 w-4" /><span className="text-sm font-medium">{e.name}</span>
                  <Badge variant="outline" className="rounded-none ml-auto capitalize">{e.role}</Badge>
                </button>
              ))}
              {employee && <button className="text-xs text-muted-foreground underline w-full pt-1" onClick={() => { setShift(null); setShiftOpen(false); }} data-testid="register-clock-out">Clock out {employee.name}</button>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
